import { fetchJaneConfig } from 'components/JaneDetailsModal/JaneDetailsModal.constants';
import { getJaneColorByStatus } from 'components/JaneDetailsModal/getJaneColorByStatus';
import { CUSTOMER_TEAMS, AGENCY_TEAMS } from 'constants/endpoints';
import { useGQLQuery } from 'hooks';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ActivityEventEnum, JaneModalJaneDetailsQuery } from 'types/graphql';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { createGQLDataContext } from 'utils/createDataContext';
import { METHOD_GET } from 'requests';
import { useUserTypeSwitch } from 'hooks/useUserTypeSwitch';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { ApiRequestConfigType } from 'requests/index.types';
import { PaginationInfo } from 'pages/CommunicationPage/DirectPage/DirectPage.types';

interface Activity {
  id: number;
  event: ActivityEventEnum;
  created_at: string;
  feedable: {
    shift_ids: number[];
  };
}

interface IFetchJaneByIdData {
  activitiesMeta: PaginationInfo;
}

export const FetchJaneByIdByIdDataContext = createGQLDataContext<IFetchJaneByIdData>(
  {} as IFetchJaneByIdData,
);

export const FetchJaneByIdByIdDataProvider: FC<{
  janeId: string;
}> = ({ children, janeId }) => {
  const [activities, setActivities] = useState([] as Activity[]);
  const [activitiesMeta, setActivitiesMeta] = useState({} as PaginationInfo);
  const { apiRequest } = useAPIActions();
  const {
    data: value,
    fetch,
    isLoading,
    refetch,
  } = useGQLQuery<JaneModalJaneDetailsQuery, JaneModalJaneDetailsQuery, { id: string | undefined }>(
    fetchJaneConfig(janeId),
  );
  useEffect(() => {
    if (isLoading) fetch();
  }, [fetch, isLoading, janeId]);

  const data = value?.fetchJane;

  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const activitiesApiConfig: ApiRequestConfigType = useUserTypeSwitch({
    B2B: {
      __typename: 'LegacyAPI',
      method: METHOD_GET,
      url: `${CUSTOMER_TEAMS}/${janeId}/activities`,
      query: {
        with_community: {
          id: currentlySelectedLocation?.value,
          type: 'Community',
        },
      },
    },
    Agency: {
      __typename: 'LegacyAPI',
      method: METHOD_GET,
      url: `${AGENCY_TEAMS}/${janeId}/activities`,
      query: {
        with_community: {
          id: currentlySelectedLocation?.value,
          type: 'AgencyCommunity',
        },
      },
    },
  });

  const getActivitiesTeamMember = useCallback(
    (pageNumber?: number) =>
      apiRequest(
        {
          ...activitiesApiConfig,
          query: {
            ...activitiesApiConfig.query,
            per_page: 10,
            page: pageNumber ?? 1,
          },
        },
        {
          onSuccess: (response) => {
            const newActivities = response?.data?.activities || [];
            setActivities((prevActivities) => {
              const mergedActivities = [
                ...prevActivities,
                ...newActivities.filter(
                  (newActivity: Activity) =>
                    !prevActivities.some(
                      (existingActivity) => existingActivity.id === newActivity.id,
                    ),
                ),
              ];
              return mergedActivities;
            });
            setActivitiesMeta(response?.data?.meta);
          },
        },
      ),
    [apiRequest, activitiesApiConfig],
  );

  useEffect(() => {
    if (isLoading) getActivitiesTeamMember();
  }, [isLoading, getActivitiesTeamMember]);

  const dotColor = getJaneColorByStatus(data?.accessStatus?.name);
  const contextValues = useMemo(() => {
    return {
      activitiesMeta,
      data,
      dotColor,
      refetch,
      fetchNextPageActivities: () => {
        getActivitiesTeamMember((activitiesMeta?.current_page ?? 1) + 1);
      },
      isLoading,
      activities,
    };
  }, [activitiesMeta, data, dotColor, refetch, isLoading, activities, getActivitiesTeamMember]);
  return (
    <FetchJaneByIdByIdDataContext.Provider value={contextValues}>
      {children}
    </FetchJaneByIdByIdDataContext.Provider>
  );
};

export const useFetchJaneByIdDataContext = () => useContext(FetchJaneByIdByIdDataContext);
